import reactUpdate, {Spec} from 'immutability-helper';
import {Component} from 'react';
import isEqual from 'react-fast-compare';
import {Unsubscribe} from 'redux';
import _ from 'underscore';

import {kv} from 'BenchlingUtil';
import {Selector} from 'notebook/ELNConstants';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {FullTableMap, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {TryInsertOptions} from 'notebook/tables/excess/FormulaUtil';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {SpreadsheetEvalStoreState} from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import SpreadsheetStore, {SpreadsheetStoreState} from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {STRING_RESOLUTION_INITIAL_STATE} from 'notebook/tables/string-resolution/reduceStringResolution';
import {
  Cells,
  CrossTableCellColors,
  Table,
  TableOpFunction,
  TableOpOptions,
} from 'notebook/tables/TableConstants';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {MODELS_INITIAL_STATE} from 'redux-stores/reduceModels';
import {getStore} from 'redux-stores/Store';
import StorableStore from 'samples/StorableStore';
import {WORKFLOWS_INITIAL_STATE} from 'workflows/reduceWorkflows';

export interface SpreadsheetEvalMixinProps {
  requestSelection?: (selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex>) => void;
}

type SpreadsheetStoreStateSubset = {
  showExcessLabels: boolean;
  cellColors?: CrossTableCellColors;
} & Pick<SpreadsheetStoreState, 'editingTable' | 'editingDocumentName'>;

type TableRoot = unknown;
type TableItem = unknown;

export interface SpreadsheetEvalMixinState extends SpreadsheetStoreStateSubset {
  computedTables: OutputTableMap | null;
  fullTableMap: FullTableMap;
  tableNamespace: string;
  tempFullTableMap: FullTableMap;
  selectorCounts: {[selectorId: number]: number};
}

// Define the component type that uses this mixin
export interface SpreadsheetEvaluatingComponent<
  P extends SpreadsheetEvalMixinProps,
  S extends SpreadsheetEvalMixinState = SpreadsheetEvalMixinState,
> extends Component<P, S> {
  _handleChangeTempFullTableMap: () => void;
  _hasFormulasDisabled: () => boolean;
  _performTableOp: (tableName: string, op: TableOpFunction, options?: TableOpOptions) => void;
  _recomputeTables: () => void;
  _reduxUnsubscribe: Unsubscribe;
  _setTableOverride: (fullTableName: string, fullTable: Cells) => void;
  _spreadsheetStoreChangeHandler: () => void;
  _spreadsheetStoreState: (tableNamespace: string) => SpreadsheetStoreStateSubset;
  _storeDependencies: StoreDependencyMap;
  _storeState: SpreadsheetEvalStoreState;
  _updateEvaluatedTableState: (fullTableMap: FullTableMap, selectors: Array<Selector>) => void;
  _updateIfStoreDependenciesChanged: () => void;
  evaluateTables: (
    fullTableMap: FullTableMap,
    selectors: Array<Selector>
  ) => {computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}};
  extraUsedTableNames?: () => Array<string>;
  getAllStoreDependencies: (props: P) => StoreDependencyMap;
  getAllStoreDependenciesFromSpreadsheetRoot?: (root: TableRoot) => StoreDependencyMap;
  getCid?: () => string;
  getContent?: (item: TableItem) => {table: Table};
  getItems?: (root: TableRoot) => Array<TableItem>;
  getRoot?: () => TableRoot;
  getType?: (item: TableItem) => string;
  getSpreadsheetEvalCacheKey?: () => string;
  getSpreadsheetTypes?: () => Array<string>;
  isTableNameUsed: (tableName: string) => boolean;
  itemsAccessor?: (op: {[i: number]: Spec<TableItem>}) => Spec<TableRoot>;
  makeFullTableMap: (props: P) => FullTableMap;
  selectorsFromProps: (props: P) => Array<Selector>;
  setContent?: (type: string, content: object) => Spec<TableItem>;
  shouldDisableFormulas?: () => boolean;
  spreadsheetRoot: (props: P) => TableRoot;
  updateRoot?: (root: TableRoot) => void;
  usedTableNames: () => Array<string | undefined>;
}

const SpreadsheetEvalMixin = {
  // Mixin maintains a map of evaluated tables.
  // Must implement:
  //   makeFullTableMap(props) - gives the mapping from table name to full table object (notePart.table)
  //   spreadsheetRoot(props) - gives the container of the tables
  //   selectorsFromProps(props) - gives the selectors array
  //
  // To disable formulas, a component may implement `shouldDisableFormulas()` that returns false.
  //
  // To perform table operations, component should implement (as specified in mediocre/editor):
  //   getCid, getContent, getItems, getRoot, getType, itemsAccessor, setContent,
  //   getSpreadsheetTypes, updateRoot
  //
  // If a component's re-evaluation relies on changes to the reduxStore, it should implement
  // getAllStoreDependenciesFromSpreadsheetRoot returning a mapping from bioentityIds
  // to a list of api identifiers.
  //
  // State:
  //   tableNamespace: a unique namespace to differentiate this component in SpreadsheetStore interactions
  //   fullTableMap: a mapping of table name to full table object present in the prop's steps.
  //   tempFullTableMap: the same mapping as fullTableMap, but it allows changes to be made to it.
  //     By calling tempTableStepUpdateHandler, tempFullTableMap is updated.
  //     By calling tempTableStepResetHandler, the specified table in tempFullTableMap is reset to
  //       the corresponding table present in fullTableMap.
  //       Note: when this component receives updated tables through props, tempFullTableMap will
  //             auto-reset to fullTableMap
  //   computedTables: a mapping of table names to evaluated tables from the tempFullTableMap.
  //     A component can access the evaluated cell from computedTables[tableName][i][j].value
  //     A component can determine if a table has been temporarily modified by comparing:
  //       tables[tableName] == computedTables[tableName]
  //   cellColors: a mapping from [table, row, column] to css classes for cells ranges/refs of
  //     the current formula.
  getInitialState<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): SpreadsheetEvalMixinState {
    const tableNamespace = _.uniqueId('table-ns');
    // eslint-disable-next-line no-restricted-syntax
    return _.extend({}, this._spreadsheetStoreState(tableNamespace), {
      tableNamespace,
    });
  },

  UNSAFE_componentWillMount<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): void {
    const fullTableMap = this.makeFullTableMap(this.props);
    const selectors = this.selectorsFromProps(this.props);
    this.setState({tempFullTableMap: fullTableMap});
    this._updateEvaluatedTableState(fullTableMap, selectors);
    this.setState({fullTableMap});
  },

  componentDidMount<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): void {
    this._storeDependencies = this.getAllStoreDependencies(this.props);
    this._storeState = {
      elnResolution: STRING_RESOLUTION_INITIAL_STATE.resolutionResultsByResolutionQuery,
      workflows: WORKFLOWS_INITIAL_STATE,
      fileSummariesById: MODELS_INITIAL_STATE.fileSummariesById,
      storablesById: {},
      storableSummariesById: {},
    };
    // Force an update whenever anything in the ELN redux tree updates (resolution results or
    // resolution loading state), when the stage runs for the entry are changed,
    // or when the bioentities with ids in this._storeDependencies update in the redux store.
    this._reduxUnsubscribe = getStore().subscribe(this._updateIfStoreDependenciesChanged);
    // Listen to StorableStore so that when the containers column of the transfer table fetches storables,
    // the table is re-rendered.
    StorableStore.addChangeListener(this._recomputeTables);
    SpreadsheetStore.addChangeListener(this._spreadsheetStoreChangeHandler);
  },

  componentWillUnmount<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): void {
    if (this._reduxUnsubscribe) {
      this._reduxUnsubscribe();
    }
    StorableStore.removeChangeListener(this._recomputeTables);
    SpreadsheetStore.removeChangeListener(this._spreadsheetStoreChangeHandler);
  },

  UNSAFE_componentWillReceiveProps<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    nextProps: P
  ): void {
    // If the protocols are the same, no update.
    if (
      this.spreadsheetRoot(this.props) === this.spreadsheetRoot(nextProps) &&
      this.selectorsFromProps(this.props) === this.selectorsFromProps(nextProps)
    ) {
      return;
    }

    // If the tableMaps are the same, no update.
    const fullTableMap = this.makeFullTableMap(nextProps);
    const selectors = this.selectorsFromProps(nextProps);
    if (isEqual(this.state.fullTableMap, fullTableMap) && selectors === this.selectorsFromProps(this.props)) {
      return;
    }
    // Update store dependencies
    this._storeDependencies = this.getAllStoreDependencies(nextProps);

    // Otherwise, update.
    this.setState({tempFullTableMap: fullTableMap});
    this._updateEvaluatedTableState(fullTableMap, selectors);
    this.setState({fullTableMap});
  },

  _handleChangeTempFullTableMap<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    newTempFullTableMap: FullTableMap
  ): void {
    this.setState({tempFullTableMap: newTempFullTableMap});
  },

  _updateIfStoreDependenciesChanged<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): void {
    this._storeState = SpreadsheetEvalUtil.updateIfStoreDependenciesChanged({
      storeState: this._storeState,
      storeDependencies: this._storeDependencies,
      tempFullTableMap: this.state.tempFullTableMap,
      handleChangeTempFullTableMap: this._handleChangeTempFullTableMap,
      makeFullTableMap: () => this.makeFullTableMap(this.props),
      selectorsFromProps: () => this.selectorsFromProps(this.props),
      updateEvaluatedTableState: this._updateEvaluatedTableState,
      fileId: null,
      isNoteLinkedGroup: false,
    });
  },

  _recomputeTables<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): void {
    this._updateEvaluatedTableState(this.state.tempFullTableMap, this.selectorsFromProps(this.props));
  },

  _hasFormulasDisabled<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): boolean {
    return this.shouldDisableFormulas == null ? false : this.shouldDisableFormulas();
  },

  evaluateTables<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    fullTableMap: FullTableMap,
    selectors: Array<Selector>
  ): {computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}} {
    return SpreadsheetEvalUtil.evaluateTables({
      fullTableMap,
      selectors,
      oldComputedTables: this.state.computedTables,
      hasFormulasDisabled: this._hasFormulasDisabled(),
      spreadsheetEvalCacheKey: this.getSpreadsheetEvalCacheKey ? this.getSpreadsheetEvalCacheKey() : null,
    });
  },

  _updateEvaluatedTableState<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    fullTableMap: FullTableMap,
    selectors: Array<Selector>
  ): void {
    const {computedTables, selectorCounts} = this.evaluateTables(fullTableMap, selectors);
    this.setState({computedTables, selectorCounts});
  },

  _spreadsheetStoreState<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    tableNamespace: string
  ): SpreadsheetStoreStateSubset {
    return SpreadsheetEvalUtil.getSpreadsheetStoreState(tableNamespace, this._hasFormulasDisabled());
  },

  _spreadsheetStoreChangeHandler<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): void {
    const newState = this._spreadsheetStoreState(this.state.tableNamespace);
    if (
      this.state.cellColors === newState.cellColors &&
      this.state.editingTable === newState.editingTable &&
      this.state.editingDocumentName === newState.editingDocumentName &&
      this.state.showExcessLabels === newState.showExcessLabels
    ) {
      return;
    }
    this.setState(newState);
  },

  setMultiEditingForTable<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    {tableName, documentName}: {tableName: string; documentName: string},
    {
      index,
      editing,
      text,
      typedValue,
      selection,
    }: {
      index: TableIndex | null;
      editing: boolean;
      text: string;
      typedValue: TypedValue | null;
      selection: {cid: string; start: number; end: number} | null;
    }
  ): void {
    SpreadsheetEvalUtil.setMultiEditingForTable({tableName, documentName}, this.state.tableNamespace, null, {
      index,
      editing,
      text,
      typedValue,
      selection,
    });
  },

  tryInsert<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    {tableName, documentName}: {tableName: string; documentName: string},
    e: TryInsertOptions
  ): boolean {
    return SpreadsheetEvalUtil.tryInsert({
      tableName,
      documentName,
      e,
      hasFormulasDisabled: this._hasFormulasDisabled(),
      tableNamespace: this.state.tableNamespace,
    });
  },

  canInsert<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    e: TryInsertOptions
  ): boolean {
    return SpreadsheetEvalUtil.canInsert({
      e,
      hasFormulasDisabled: this._hasFormulasDisabled(),
      tableNamespace: this.state.tableNamespace,
      editingTable: this.state.editingTable,
      editingDocumentName: null,
    });
  },

  /**
   * Performs an operation on a table, updating the appropriate react components
   * @param  {Number} index   The index of the targetItem containing the table with targetTableName
   * @param  {Function} op    (originalTableMap, targetTableName) -> {tables: newTableMap, box}, where
   *                            originalTableMap {Object}  Maps original names of tables to original tables.
   *                            targetTableName {String}   The name of the table being directly modified
   *                            newTableMap {Object}       Maps *original* names of tables to new tables.
   *                            box {Object}               {minI, maxI, minJ, maxJ}, or null if no selection
   *                                                       is specified.
   * @return null
   */
  _performTableOp<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    tableName: string,
    op: TableOpFunction,
    options?: TableOpOptions
  ): void {
    SpreadsheetEvalUtil.performTableOp<TableRoot, TableItem>({
      tableName,
      fileId: null,
      op,
      options,
      spreadsheetTypes: this.getSpreadsheetTypes!(),
      computedTables: this.state.computedTables!,
      getItems: this.getItems!,
      getRoot: this.getRoot!,
      getType: this.getType!,
      getCid: this.getCid!,
      getContent: this.getContent!,
      setContent: this.setContent!,
      requestSelection: this.props.requestSelection!,
      updateRoot: this.updateRoot!,
      itemsAccessor: this.itemsAccessor!,
    });
  },

  // We disable expanding a multiselect via drag while the a table is actively being edited, as this triggers
  // the child table to clear its selection (via multiMouseLeaveHandler), however, the table is still
  // identified by state.editingTable. We let the table keep its selection so that it can handle finalizing
  // the edit.
  disallowMultiExpand<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): boolean {
    return this.state.editingTable != null;
  },

  /**
   * Determines whether tableName is used anywhere within the existing note.
   *
   * @param  {String} tableName    name we are checking for
   * @return {Boolean}             true if tableName has been used in existing note and false otherwise.
   */
  isTableNameUsed<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    tableName: string
  ): boolean {
    return this.usedTableNames().includes(tableName);
  },

  getAllStoreDependencies<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    props: P
  ): StoreDependencyMap {
    // Not all users of this mixin define `getAllStoreDependenciesFromSpreadsheetRoot`.
    if (!this.getAllStoreDependenciesFromSpreadsheetRoot) {
      return {};
    }
    return this.getAllStoreDependenciesFromSpreadsheetRoot(this.spreadsheetRoot(props));
  },

  updateSelectors<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    newSelectors: Array<Selector>
  ): void {
    // @ts-expect-error reactUpdate not getting along with Spec
    const root = reactUpdate(this.getRoot!(), {$merge: {selectors: newSelectors}});
    this.updateRoot!(root);
  },

  // Utility methods

  usedTableNames<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): Array<string> {
    // eslint-disable-next-line no-restricted-syntax
    return _.keys(this.state.fullTableMap).concat(
      // @ts-expect-error .concat has some weird types that the undefined does not get along with
      typeof this.extraUsedTableNames === 'function' ? this.extraUsedTableNames() : undefined
    );
  },

  tempTableStepUpdateHandler<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    fullTable: Table
  ): void {
    const {table} = this.state.tempFullTableMap[fullTable.name];
    if (fullTable.table !== table) {
      this._setTableOverride(fullTable.name, fullTable.table);
    }
  },

  tempTableStepResetHandler<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    name: string
  ): void {
    const newTable = this.state.fullTableMap[name].table;
    this._setTableOverride(name, newTable);
  },

  _setTableOverride<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>,
    tableName: string,
    table: Cells
  ): void {
    // @ts-expect-error reactUpdate is not getting along with kv. why IS coffeescript
    const tempFullTableMap = reactUpdate(this.state.tempFullTableMap, kv(tableName, {$merge: {table}}));
    const selectors = this.selectorsFromProps(this.props);
    this.setState({tempFullTableMap});
    this._updateEvaluatedTableState(tempFullTableMap, selectors);
  },

  getDataForTableItemProps<P extends SpreadsheetEvalMixinProps, S extends SpreadsheetEvalMixinState>(
    this: SpreadsheetEvaluatingComponent<P, S>
  ): Omit<SpreadsheetEvalMixinState, 'editingDocumentName' | 'tempFullTableMap'> {
    return {
      tableNamespace: this.state.tableNamespace,
      showExcessLabels: this.state.showExcessLabels,
      computedTables: this.state.computedTables,
      cellColors: this.state.cellColors,
      editingTable: this.state.editingTable,
      selectorCounts: this.state.selectorCounts,
      fullTableMap: this.state.fullTableMap,
    };
  },
};

export default SpreadsheetEvalMixin;
